/**
 * Offices (geofence centres) and shift policies.
 *
 * These two are on one screen because they only make sense together: a
 * geofenced policy is meaningless without an office to measure from, and an
 * office does nothing until a policy points at it.
 *
 * Managers reach this screen — reading offices and policies only needs a
 * signed-in user — but every write here requires company_admin. The add
 * controls are therefore hidden from a manager rather than shown and left to
 * fail on submit, matching how CompaniesPage hides its create button.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import { captureLocation } from '../../lib/geolocation';
import DeleteRecordDialog from '../../components/DeleteRecordDialog';
import { isCompanyAdmin, useAuth } from '../../store/auth';
import type { Office, Page, ShiftPolicy } from '../../types';


const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function WorkplacesPage() {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.user);
  const canEdit = isCompanyAdmin(me);
  const [showOffice, setShowOffice] = useState(false);
  const [deletingOffice, setDeletingOffice] = useState<Office | null>(null);
  const [showPolicy, setShowPolicy] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<ShiftPolicy | null>(null);

  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['admin-offices'],
    queryFn: async () => (await client.get('/api/v1/admin/offices')).data,
  });

  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['admin-shift-policies'],
    queryFn: async () => (await client.get('/api/v1/admin/shift-policies')).data,
  });

  const createOffice = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/offices', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-offices'] });
      setShowOffice(false);
      toast.success('Office added');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const removeLocation = useMutation({
    mutationFn: async ({
      officeId,
      locationId,
    }: {
      officeId: string;
      locationId: string;
    }) =>
      (
        await client.delete(
          `/api/v1/admin/offices/${officeId}/locations/${locationId}`,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-offices'] });
      toast.success('Building removed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const createPolicy = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/shift-policies', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shift-policies'] });
      setShowPolicy(false);
      toast.success('Shift policy added');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Edits apply to shifts started *after* they are saved.
   *
   * A running session snapshotted its settings when it opened and keeps the
   * ping schedule already written to the table, so changing the cadence at
   * eleven o'clock cannot rescore somebody's morning. Said on the form as
   * well, because it is the first question anyone asks before saving.
   */
  const updatePolicy = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      (await client.patch(`/api/v1/admin/shift-policies/${id}`, body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shift-policies'] });
      setEditingPolicy(null);
      toast.success('Shift policy updated');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-8">
      {/* --- Offices --- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Offices (Geofence Centres)</h2>
          {canEdit && (
            <button type="button" className="btn-secondary" onClick={() => setShowOffice((v) => !v)}>
              <Plus className="h-4 w-4" />
              {showOffice ? 'Cancel' : 'Add office'}
            </button>
          )}
        </div>

        {canEdit && showOffice && (
          <OfficeForm submitting={createOffice.isPending} onSubmit={createOffice.mutate} />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {offices?.items.map((office) => (
            <div key={office.id} className="card">
              <h3 className="flex items-center gap-2 font-medium text-gray-900">
                <MapPin className="h-4 w-4 text-indigo-600" />
                {office.name}
              </h3>

              {/* One card, however many buildings. The office's own
                  coordinates are the first entry rather than a separate
                  section: a site that spans two towers is one workplace, and
                  splitting it visually would say there are two. */}
              <ul className="mt-2 space-y-2">
                {[
                  {
                    id: office.id,
                    label: (office.locations ?? []).length > 0 ? 'Main' : null,
                    latitude: office.latitude,
                    longitude: office.longitude,
                    radiusM: office.radiusM,
                    removable: false,
                  },
                  ...(office.locations ?? []).map((l) => ({
                    id: l.id,
                    label: l.label,
                    latitude: l.latitude,
                    longitude: l.longitude,
                    radiusM: l.radiusM,
                    removable: true,
                  })),
                ].map((point) => (
                  <li key={point.id} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {point.label && (
                          <span className="font-medium text-gray-800">
                            {point.label}
                          </span>
                        )}
                        <p className="font-mono text-xs text-gray-500">
                          {point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}
                        </p>
                        <p className="text-xs text-gray-600">
                          Radius <strong>{point.radiusM} m</strong>
                        </p>
                        <a
                          className="text-xs text-indigo-600 hover:underline"
                          href={`https://www.google.com/maps?q=${point.latitude},${point.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Verify on a map ↗
                        </a>
                      </div>
                      {point.removable && canEdit && (
                        <button
                          type="button"
                          className="btn-ghost text-xs text-red-700"
                          onClick={() =>
                            removeLocation.mutate({
                              officeId: office.id,
                              locationId: point.id,
                            })
                          }
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {canEdit && (
                <button
                  type="button"
                  className="btn-ghost mt-3 text-xs text-red-700"
                  onClick={() => setDeletingOffice(office)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete office
                </button>
              )}

              {canEdit && (
                <AddLocationForm
                  officeId={office.id}
                  onAdded={() =>
                    queryClient.invalidateQueries({ queryKey: ['admin-offices'] })
                  }
                />
              )}
            </div>
          ))}
          {offices?.items.length === 0 && (
            <p className="text-sm text-gray-500">
              No offices yet. Add one before creating a geofenced shift policy.
            </p>
          )}
        </div>
      </section>

      {/* --- Shift policies --- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Shift Policies</h2>
          {canEdit && (
            <button type="button" className="btn-secondary" onClick={() => setShowPolicy((v) => !v)}>
              <Plus className="h-4 w-4" />
              {showPolicy ? 'Cancel' : 'Add policy'}
            </button>
          )}
        </div>

        {canEdit && showPolicy && !editingPolicy && (
          <PolicyForm
            offices={offices?.items ?? []}
            submitting={createPolicy.isPending}
            onSubmit={createPolicy.mutate}
          />
        )}

        {canEdit && editingPolicy && (
          <PolicyForm
            key={editingPolicy.id}
            policy={editingPolicy}
            offices={offices?.items ?? []}
            submitting={updatePolicy.isPending}
            onSubmit={(body) =>
              updatePolicy.mutate({ id: editingPolicy.id, body })
            }
            onCancel={() => setEditingPolicy(null)}
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {policies?.items.map((policy) => (
            <div key={policy.id} className="card">
              <div className="flex items-start justify-between gap-2">
                <h3 className="flex items-center gap-2 font-medium text-gray-900">
                  <Clock className="h-4 w-4 text-indigo-600" />
                  {policy.name}
                </h3>
                {canEdit && (
                  <button
                    type="button"
                    className="btn-ghost px-2 py-1 text-xs"
                    onClick={() => {
                      setShowPolicy(false);
                      setEditingPolicy(policy);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-600">
                {policy.startTime.slice(0, 5)}–{policy.endTime.slice(0, 5)} ·{' '}
                {policy.workingDays
                  .split(',')
                  .map((d) => DAY_NAMES[Number(d)])
                  .join(' ')}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {/* Named rather than shown as a cadence nobody is on: a policy
                    with check-ins off schedules none, and reading "every 10m"
                    off it would describe prompts that never arrive. */}
                {policy.pingEnabled ? (
                  <>
                    <span className="badge bg-gray-100 text-gray-600">
                      every {policy.pingIntervalMinutes}m
                    </span>
                    <span className="badge bg-gray-100 text-gray-600">
                      ±{policy.pingJitterMinutes}m jitter
                    </span>
                  </>
                ) : (
                  <span className="badge bg-amber-100 text-amber-700">
                    check-ins off
                  </span>
                )}
                <span
                  className={`badge ${
                    policy.geofenceRequired
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {policy.geofenceRequired ? 'office-gated' : 'field role'}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Present at {Math.round(policy.fullDayThreshold * 100)}%, half-day at{' '}
                {Math.round(policy.halfDayThreshold * 100)}%
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Same dialog the candidate and call deletes use, so a refusal reads
          identically wherever it comes from. It asks the deletable endpoint
          first, which is what lets it name the blockers before anybody
          presses the button rather than after. */}
      {deletingOffice && (
        <DeleteRecordDialog
          noun="office"
          checkUrl={`/api/v1/admin/offices/${deletingOffice.id}/deletable`}
          deleteUrl={`/api/v1/admin/offices/${deletingOffice.id}`}
          onClose={() => setDeletingOffice(null)}
          onDeleted={() => {
            setDeletingOffice(null);
            queryClient.invalidateQueries({ queryKey: ['admin-offices'] });
          }}
        />
      )}
    </div>
  );
}

function OfficeForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radiusM: '150',
  });
  const [locating, setLocating] = useState(false);

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: form.name.trim(),
          address: form.address.trim() || null,
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          radiusM: Number(form.radiusM),
        });
      }}
    >
      <div>
        <label className="label" htmlFor="o-name">Office Name</label>
        <input
          spellCheck={false}
          id="o-name"
          className="input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="o-radius">Radius (Metres)</label>
        <input
          id="o-radius"
          type="number"
          min={25}
          max={5000}
          className="input"
          value={form.radiusM}
          onChange={(e) => setForm({ ...form, radiusM: e.target.value })}
          required
        />
        <p className="mt-1 text-xs text-gray-500">
          Below ~50 m, ordinary GPS error makes staff flicker in and out.
        </p>
      </div>
      <div>
        <label className="label" htmlFor="o-lat">Latitude</label>
        <input
          spellCheck={false}
          id="o-lat"
          className="input font-mono"
          value={form.latitude}
          onChange={(e) => setForm({ ...form, latitude: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="o-lng">Longitude</label>
        <input
          spellCheck={false}
          id="o-lng"
          className="input font-mono"
          value={form.longitude}
          onChange={(e) => setForm({ ...form, longitude: e.target.value })}
          required
        />
      </div>

      <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Add office
        </button>
        {/* Standing in the office is the most reliable way to get the centre
            right; typing coordinates by hand is error-prone. */}
        <button
          type="button"
          className="btn-secondary"
          disabled={locating}
          onClick={async () => {
            setLocating(true);
            const geo = await captureLocation();
            setLocating(false);
            if (geo.ok) {
              setForm((f) => ({
                ...f,
                latitude: geo.location.latitude.toFixed(6),
                longitude: geo.location.longitude.toFixed(6),
              }));
              toast.success(`Captured (±${Math.round(geo.location.accuracyM)} m)`);
            } else {
              toast.error(geo.message);
            }
          }}
        >
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          Use my current location
        </button>
      </div>
    </form>
  );
}

/**
 * One form for creating a policy and for editing one.
 *
 * Shared rather than copied because the coherence rule spanning interval,
 * jitter and grace is enforced on both paths server-side, and two forms would
 * eventually offer different fields to the same rule — which is how you get a
 * policy that can be created but not edited back.
 */
function PolicyForm({
  policy,
  offices,
  submitting,
  onSubmit,
  onCancel,
}: {
  /** Absent when creating. Present when editing, and seeds every field. */
  policy?: ShiftPolicy;
  offices: Office[];
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
  onCancel?: () => void;
}) {
  const editing = policy != null;
  const [form, setForm] = useState({
    name: policy?.name ?? '',
    // What a new policy starts as. Only a starting point — every field
    // here is editable before saving and afterwards, and no existing
    // policy is affected by changing this.
    startTime: policy?.startTime.slice(0, 5) ?? '08:30',
    endTime: policy?.endTime.slice(0, 5) ?? '17:30',
    workingDays: policy?.workingDays.split(',') ?? ['1', '2', '3', '4', '5', '6'],
    pingEnabled: policy?.pingEnabled ?? true,
    pingIntervalMinutes: policy?.pingIntervalMinutes ?? 10,
    pingGraceMinutes: policy?.pingGraceMinutes ?? 4,
    pingJitterMinutes: policy?.pingJitterMinutes ?? 5,
    geofenceRequired: policy?.geofenceRequired ?? true,
    officeId: policy?.officeId ?? '',
  });

  /**
   * The rule the server will apply, applied here too so the reason arrives
   * before the round trip rather than as a 422.
   *
   * Grace runs from the moment a check-in fires and jitter moves that moment
   * forward inside its slot, so a prompt stays answerable until
   * slot + jitter + grace. Let that exceed the interval and two prompts are
   * live at once.
   */
  const cadenceProblem =
    form.pingJitterMinutes + form.pingGraceMinutes > form.pingIntervalMinutes
      ? 'Jitter plus grace cannot exceed the check-in interval — a prompt delayed by jitter would still be open when the next one fires.'
      : null;

  const toggleDay = (d: string) =>
    setForm((f) => ({
      ...f,
      workingDays: f.workingDays.includes(d)
        ? f.workingDays.filter((x) => x !== d)
        : [...f.workingDays, d].sort(),
    }));

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (cadenceProblem) return;
        onSubmit({
          name: form.name.trim(),
          startTime: `${form.startTime}:00`,
          endTime: `${form.endTime}:00`,
          workingDays: form.workingDays.join(','),
          pingEnabled: form.pingEnabled,
          pingIntervalMinutes: form.pingIntervalMinutes,
          pingGraceMinutes: form.pingGraceMinutes,
          pingJitterMinutes: form.pingJitterMinutes,
          geofenceRequired: form.geofenceRequired,
          officeId: form.officeId || null,
        });
      }}
    >
      <div>
        <label className="label" htmlFor="p-name">Policy Name</label>
        <input
          spellCheck={false}
          id="p-name"
          className="input"
          placeholder="Office Staff 8:30–5:30"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label" htmlFor="p-start">Start</label>
          <input
            id="p-start"
            type="time"
            className="input"
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor="p-end">End</label>
          <input
            id="p-end"
            type="time"
            className="input"
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })}
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <span className="label">Working days</span>
        <div className="flex flex-wrap gap-1.5">
          {DAY_NAMES.slice(1).map((label, i) => {
            const value = String(i + 1);
            const on = form.workingDays.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleDay(value)}
                className={`rounded-lg px-3 py-1.5 text-sm ring-1 ${
                  on
                    ? 'bg-indigo-600 text-white ring-indigo-600'
                    : 'bg-white text-gray-600 ring-gray-300'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* The switch, which until now was a column nothing read. It is above
          the cadence fields because turning it off makes them moot. */}
      <div className="sm:col-span-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.pingEnabled}
            onChange={(e) => setForm({ ...form, pingEnabled: e.target.checked })}
          />
          Ask for check-ins during the shift
        </label>
        <p className="mt-1 text-xs text-gray-500">
          Off means no prompts at all. Shifts still start and end and the hours
          are still recorded — the day is reported as unmonitored rather than
          absent, because nothing was ever expected.
        </p>
      </div>

      <div className={form.pingEnabled ? '' : 'opacity-40'}>
        <label className="label" htmlFor="p-interval">Check-in Every (Minutes)</label>
        <input
          id="p-interval"
          type="number"
          min={5}
          max={240}
          className="input"
          value={form.pingIntervalMinutes}
          onChange={(e) =>
            setForm({ ...form, pingIntervalMinutes: Number(e.target.value) })
          }
        />
      </div>
      <div className={form.pingEnabled ? '' : 'opacity-40'}>
        <label className="label" htmlFor="p-jitter">Random Jitter (Minutes)</label>
        <input
          id="p-jitter"
          type="number"
          min={0}
          max={form.pingIntervalMinutes - form.pingGraceMinutes}
          className="input"
          value={form.pingJitterMinutes}
          onChange={(e) => setForm({ ...form, pingJitterMinutes: Number(e.target.value) })}
        />
        <p className="mt-1 text-xs text-gray-500">
          Without jitter the prompt time is predictable and easy to game.
        </p>
      </div>

      <div className={form.pingEnabled ? '' : 'opacity-40'}>
        <label className="label" htmlFor="p-grace">Answer Within (Minutes)</label>
        <input
          id="p-grace"
          type="number"
          min={1}
          max={120}
          className="input"
          value={form.pingGraceMinutes}
          onChange={(e) =>
            setForm({ ...form, pingGraceMinutes: Number(e.target.value) })
          }
        />
        <p className="mt-1 text-xs text-gray-500">
          After this the check-in is recorded as missed. Editable because it
          has to move with the interval — shortening the interval without
          shortening this leaves two prompts open at once.
        </p>
      </div>

      {cadenceProblem && (
        <p className="sm:col-span-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {cadenceProblem}
        </p>
      )}

      <div className="sm:col-span-2 space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={form.geofenceRequired}
            onChange={(e) => setForm({ ...form, geofenceRequired: e.target.checked })}
          />
          Require staff to be at the office
        </label>
        <p className="text-xs text-gray-500">
          Leave this off for field roles. Their location is still recorded — they
          just are not marked absent for being at a client site.
        </p>

        {form.geofenceRequired && (
          <div>
            <label className="label" htmlFor="p-office">Office</label>
            <select
              id="p-office"
              className="input"
              value={form.officeId}
              onChange={(e) => setForm({ ...form, officeId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {offices.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="sm:col-span-2 space-y-2">
        <div className="flex items-center gap-2">
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || cadenceProblem != null}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? 'Save changes' : 'Create policy'}
          </button>
          {onCancel && (
            <button type="button" className="btn-ghost" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>

        {/* The first question anybody asks before saving, answered where they
            are about to save. A running shift keeps the schedule already
            written to it, so nobody's morning is rescored at lunchtime. */}
        {editing && (
          <p className="text-xs text-gray-500">
            Changes apply to shifts started after you save. Anyone already
            clocked in keeps today's check-in schedule.
          </p>
        )}
      </div>
    </form>
  );
}


/**
 * Add another building to an office that already exists.
 *
 * Inline on the office's own card rather than behind a separate screen: the
 * thing being extended is this workplace, and the coordinates it already has
 * are what you check the new pair against.
 */
function AddLocationForm({
  officeId,
  onAdded,
}: {
  officeId: string;
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    label: '',
    latitude: '',
    longitude: '',
    radiusM: '150',
  });

  const add = useMutation({
    mutationFn: async () =>
      (
        await client.post(`/api/v1/admin/offices/${officeId}/locations`, {
          label: form.label.trim() || null,
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          radiusM: Number(form.radiusM),
        })
      ).data,
    onSuccess: () => {
      setForm({ label: '', latitude: '', longitude: '', radiusM: '150' });
      setOpen(false);
      onAdded();
      toast.success('Building added');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost mt-3 text-xs"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Add another building
      </button>
    );
  }

  return (
    <form
      className="mt-3 space-y-2 border-t border-gray-100 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <input
        className="input text-xs"
        placeholder="Building name — Tower B, Annexe"
        value={form.label}
        onChange={(e) => setForm({ ...form, label: e.target.value })}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="input text-xs"
          placeholder="Latitude"
          required
          value={form.latitude}
          onChange={(e) => setForm({ ...form, latitude: e.target.value })}
        />
        <input
          className="input text-xs"
          placeholder="Longitude"
          required
          value={form.longitude}
          onChange={(e) => setForm({ ...form, longitude: e.target.value })}
        />
      </div>
      <input
        className="input text-xs"
        placeholder="Radius in metres"
        value={form.radiusM}
        onChange={(e) => setForm({ ...form, radiusM: e.target.value })}
      />
      {/* Standing in the building is the most reliable way to get its
          coordinates, and the person adding it usually is. */}
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={async () => {
          const geo = await captureLocation();
          if (geo.ok) {
            setForm((f) => ({
              ...f,
              latitude: String(geo.location.latitude),
              longitude: String(geo.location.longitude),
            }));
          } else {
            toast.error(geo.message);
          }
        }}
      >
        Use my current location
      </button>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary text-xs" disabled={add.isPending}>
          Add
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
